/**
 * Map View Builder - Helper functions for building and wiring the map view UI
 *
 * Extracted from navigateToMapView() to improve maintainability.
 * Each function has a single, focused responsibility.
 */

import { formatGameDateWithDays } from './calendar.js';
import { isChromiumBrowser } from './file-auto-save.js';

/**
 * Build the HTML structure for the map view
 * @param {Object} session - Current session
 * @returns {string} HTML string for map view
 */
export function buildMapViewHTML(session) {
  const currentDateText = session.calendar
    ? formatGameDateWithDays(session.calendar.currentDate, session.calendar.daysElapsed)
    : 'No Calendar';

  // Check if auto-save should be available (Chromium browser + non-demo session)
  const showAutoSave = isChromiumBrowser() && !session.isDemo;

  return `
    <div class="app-container">
      <header class="app-header">
        <div class="header-left">
          <button id="home-btn" class="btn btn-icon" title="Home" style="margin-right: 1rem;">
            <i class="fas fa-home"></i>
          </button>
          <h1>🗺️ ${session.mapName}</h1>
          <div class="date-menu-container">
            <div id="current-date-display" class="current-date-display">${currentDateText}</div>
            <div id="date-dropdown" class="date-dropdown" style="display: none;">
              <button class="date-menu-item" data-advance="1">
                <i class="fas fa-plus"></i> +1 Day
              </button>
              <button class="date-menu-item" data-advance="7">
                <i class="fas fa-plus"></i> +7 Days
              </button>
              <button class="date-menu-item" data-advance="30">
                <i class="fas fa-plus"></i> +30 Days
              </button>
              <button class="date-menu-item" id="open-calendar-from-dropdown">
                <i class="fas fa-calendar-alt"></i> Open Calendar
              </button>
            </div>
          </div>
        </div>
        <div class="header-right">
          <button id="player-view-btn" class="btn btn-accent" title="Open Player View">
            <i class="fas fa-tv"></i> Player View
          </button>
          <button id="calendar-btn" class="btn btn-icon" title="Calendar">
            <i class="fas fa-calendar-alt"></i>
          </button>
          <button id="time-travel-toggle-btn" class="btn btn-icon" title="Time Travel">
            <i class="fas fa-clock"></i>
          </button>
          <button id="undo-btn" class="btn btn-icon" title="Undo (Ctrl+Z)" disabled>
            <i class="fas fa-undo"></i>
          </button>
          <button id="redo-btn" class="btn btn-icon" title="Redo (Ctrl+Y)" disabled>
            <i class="fas fa-redo"></i>
          </button>
          <button id="toggle-ids-btn" class="btn btn-icon" title="Toggle Display IDs">
            <i class="fas fa-hashtag"></i>
          </button>
          <button id="save-btn" class="btn btn-icon" title="Save Session (Ctrl+S)">
            <i class="fas fa-save"></i> Save
          </button>
          ${showAutoSave ? `
          <button id="auto-save-btn" class="btn btn-icon" title="Link Auto-Save File" data-linked="false">
            <i class="fas fa-link"></i> <span id="auto-save-text">Link File</span>
          </button>
          <span id="auto-save-indicator" class="auto-save-indicator" style="display: none;">
            <i class="fas fa-check-circle"></i> <span id="auto-save-status">Auto-saved</span>
          </span>
          ` : ''}
          <button id="print-btn" class="btn btn-icon" title="Print Map">
            <i class="fas fa-print"></i> Print
          </button>
          <button id="refresh-map-btn" class="btn btn-icon" title="Refresh Map (fixes tile misalignment)">
            <i class="fas fa-sync-alt"></i>
          </button>
          <span id="unsaved-indicator" class="unsaved-indicator" style="display: none;">
            <i class="fas fa-circle"></i> Unsaved changes
          </span>
          <div class="utilities-menu-container">
            <button id="settings-btn" class="btn btn-icon" title="Settings">
              <i class="fas fa-cog"></i>
            </button>
            <div id="utilities-dropdown" class="utilities-dropdown" style="display: none;">
              <button class="utilities-menu-item" data-action="sync-tiles">
                <i class="fas fa-sync"></i>
                <span>Sync Tiles</span>
              </button>
              <button class="utilities-menu-item" data-action="icon-attribution">
                <i class="fas fa-info-circle"></i>
                <span>Icon Attribution</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div id="time-travel-slider" class="time-travel-slider" style="display: none;">
        <div class="time-travel-container">
          <label for="time-slider" class="time-travel-label">Time Travel:</label>
          <button id="time-prev-btn" class="btn-icon btn-time-nav" title="Previous Day">
            <i class="fas fa-chevron-left"></i>
          </button>
          <input type="range" id="time-slider" class="time-slider" min="1" max="1" value="1" step="1">
          <button id="time-next-btn" class="btn-icon btn-time-nav" title="Next Day">
            <i class="fas fa-chevron-right"></i>
          </button>
          <span id="time-display" class="time-display">Day 1</span>
          <span id="historical-indicator" class="historical-indicator" style="display: none;">Viewing: Historical</span>
          <button id="jump-to-present-btn" class="btn-small btn-primary" style="display: none;" title="Return to present">
            <i class="fas fa-map-pin"></i> Present
          </button>
          <select id="bookmark-jump" class="bookmark-jump" title="Jump to bookmark">
            <option value="">Jump to Bookmark...</option>
          </select>
        </div>
      </div>

      <main class="main-content">
        <left-sidebar id="left-sidebar"></left-sidebar>

        <div class="map-container">
          <hex-map id="main-map"></hex-map>
          <div class="map-controls">
            <button class="btn btn-icon" title="Zoom In">
              <i class="fas fa-search-plus"></i>
            </button>
            <button class="btn btn-icon" title="Zoom Out">
              <i class="fas fa-search-minus"></i>
            </button>
            <button class="btn btn-icon" title="Reset View">
              <i class="fas fa-compress"></i>
            </button>
          </div>
        </div>

        <note-editor id="note-editor"></note-editor>
        <icon-picker id="icon-picker"></icon-picker>
        <token-creator id="token-creator"></token-creator>
        <print-modal id="print-modal"></print-modal>
      </main>
      <debug-panel></debug-panel>
    </div>
  `;
}

/**
 * Initialize the hex-map component with session and map data
 * @param {Object} session - Current session
 * @param {Object} mapData - Map data
 * @returns {Promise<HTMLElement>} The initialized hex-map element
 */
export async function initializeMapComponent(session, mapData) {
  const hexMap = document.getElementById('main-map');
  if (hexMap) {
    await hexMap.loadMap(session, mapData);
  }
  return hexMap;
}
